import { SearchInput } from "@/components/SearchInput";
import { Link, useNavigate } from "react-router";
import { Badge } from "./components/Badge";
import { Helmet } from "react-helmet";
import { Card } from "./components/ui/card";
import UserPosts from "./components/UserPosts";
import { useInView } from "react-intersection-observer";
import { cn } from "./lib/utils";
import { FAQ } from "./components/FAQ";
import { Button } from "./components/ui/button";
import FeaturedProfiles from "./components/FeaturedProfiles";


const App = () => {
  const navigate = useNavigate();
  const handleSearch = async (actor: string) => {
    navigate(`/${actor}`);
  }

  // TODO: Test accounts for issues
  // 7. elonaoc.bsky.social (empty graph)

  const [engagementRef, engagementInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [wordCloudRef, wordCloudInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [trendsRef, trendsInView] = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <>
      <Helmet>
        <title>Bluesky Insights — Free Insights for any Bluesky profile</title>
        <meta name="description" content="Get free beautiful analytics for any Bluesky user. No signup required, start by entering a username or handle." />
      </Helmet>
      <div className="flex flex-col h-full">
        <main className="flex-grow z-10 ">
          <div className="mx-auto h-full flex flex-col">
            <div className="bg-gradient-to-b from-primary/5 to-transparent pt-32">
              <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center mb-16 md:mb-32 animate-fade-up">
                <Badge>New: Bluesky Bookmarks Viewer!</Badge>
                <h1 className="mt-6 text-4xl md:text-7xl font-bold tracking-tight sm:text-5xl">
                  Free {" "}
                  <span className="text-primary">Insights {" "}</span>
                  for Your Bluesky Profile
                </h1>
                <h2 className="mt-6 md:text-lg text-muted-foreground">
                  Get beautiful data-driven <span className="font-bold">Bluesky Analytics</span> for any user. No signup required.
                </h2>
                <div className="mt-20">
                  <SearchInput onSearch={handleSearch} />
                </div>
              </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 flex flex-col gap-16 mb-24">
              <section
                ref={engagementRef}
                className={cn(
                  "grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-500 transform",
                  engagementInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                )}
              >
                <div className="space-y-6">
                  <span className="px-4 py-1 bg-primary/10 rounded-full text-sm font-medium inline-block">
                    Engagement Metrics
                  </span>
                  <h2 className="text-2xl md:text-4xl font-semibold tracking-tight">Track Your Growth</h2>
                  <div className="space-y-4">
                    <p className="text-muted-foreground">
                      Monitor likes, replies, and reposts over time to understand your content's performance.
                    </p>
                    <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                      <li>Post engagement rates and trends</li>
                      <li>Audience interaction patterns</li>
                      <li>Content performance metrics</li>
                      <li>Comprehensive engagement trends</li>
                    </ul>
                  </div>
                </div>
                <Card className="overflow-hidden border shadow-lg lg:ml-auto">
                  <img
                    src="https://res.cloudinary.com/dc7wg0knu/image/upload/v1734192103/Screenshot_2024-12-14_at_9.31.30_PM_v0iery.png"
                    alt="Engagement Metrics Dashboard showing detailed analytics of post performance"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </Card>
              </section>

              <section
                ref={wordCloudRef}
                className={cn(
                  "grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-500 transform",
                  wordCloudInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                )}
              >
                <div className="space-y-6 lg:order-first">
                  <span className="px-4 py-1 bg-primary/10 rounded-full text-sm font-medium inline-block">
                    Content Analysis
                  </span>
                  <h2 className="text-2xl md:text-4xl font-semibold tracking-tight">Bluesky Word Cloud</h2>
                  <div className="space-y-4">
                    <p className="text-muted-foreground">
                      Dive into a vibrant word cloud generated from your posts. Instantly visualize the most frequent topics and keywords in your content.
                    </p>
                    <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                      <li>Spot dominant themes in your posts</li>
                      <li>Identify recurring keywords and topics</li>
                      <li>Gain a visual summary of your content focus</li>
                    </ul>
                  </div>
                </div>
                <Card className="overflow-hidden border shadow-lg lg:ml-auto">
                  <img
                    src="https://res.cloudinary.com/dc7wg0knu/image/upload/v1734192503/word-cloud_f7ie0y.png"
                    alt="Interactive Word Cloud Visualization showing trending topics and keywords"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </Card>
              </section>

              <section
                ref={trendsRef}
                className={cn(
                  "grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-500 transform",
                  trendsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                )}
              >
                <div className="space-y-6">
                  <span className="px-4 py-1 bg-primary/10 rounded-full text-sm font-medium inline-block">
                    Posting Trends
                  </span>
                  <h2 className="text-2xl md:text-4xl font-semibold tracking-tight">
                    Analyze Activity Patterns
                  </h2>
                  <div className="space-y-4">
                    <p className="text-muted-foreground">
                      Understand when users are most active with our detailed hourly posting trends. The graph helps you uncover patterns and optimize your interactions.
                    </p>
                    <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                      <li>Hourly posting frequency over 24 hours</li>
                      <li>Analyze activity patterns for any user</li>
                      <li>Identify peak activity times to improve engagement</li>
                      <li>Data-driven schedule optimization</li>
                    </ul>
                  </div>
                </div>
                <Card className="overflow-hidden border shadow-lg lg:ml-auto">
                  <img
                    src="https://res.cloudinary.com/dc7wg0knu/image/upload/v1734193229/trends_eqttl1.png"
                    alt="Detailed Activity Trends Chart showing optimal posting times and engagement patterns"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </Card>
              </section>
            </div>
            {/*<div className="my-12">
              <UserPosts />
            </div>*/}
            <div className="my-12 max-w-4xl mx-auto px-4">
              <h2 className="text-xl text-center font-semibold mb-4 p-2 rounded-full max-w-lg mx-auto">
                Our Sponsors
              </h2>
              <FeaturedProfiles />
            </div>
            <div>
              <FAQ />
            </div>
          </div>
        </main>

      </div>
    </>
  );
};

export default App;
